import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faSearch,
  faFilter,
  faEye,
  faTruck,
  faCheckCircle,
  faMoneyBillWave,
  faCreditCard,
  faBox,
  faMapMarkerAlt,
  faUser,
  faPhone,
  faCalendarAlt,
  faChevronRight,
  faArrowLeft,
  faExclamationTriangle,
  faClock,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import { shipmentService } from '../../../../services/shipmentService';
import { paymentsService } from '../../../../services/paymentsService';
import { formatTrackingCode } from '../../../../utils/formatters';

const MyShipments = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Fetch shipments data from backend
  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await shipmentService.getMy(token);
        const list = Array.isArray(resp.shipments || resp) ? (resp.shipments || resp) : [];
        const mapped = list.map(s => ({
          id: formatTrackingCode(s.trackingCode || s._id || s.id || '—'),
          shipmentId: s._id || s.id || null,
          trackingCode: formatTrackingCode(s.trackingCode || ''),
          date: s.createdAt
            ? new Date(s.createdAt).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : s.date || '',
          senderName: s.senderName || '',
          receiverName: s.receiverName || '',
          receiverEmail: s.receiverEmail || '',
          pickupLocation: s.pickupLocation || s.senderAddress || '',
          dropoffLocation: s.receiverAddress || s.receiverState || '',
          serviceType: s.serviceType || 'interstate',
          paymentType: s.paymentType || 'payNow',
          paymentStatus: s.paymentStatus || 'Pending',
          paymentMethod: s.paymentMethod || '',
          paymentReferenceCode: s.paymentReferenceCode || '',
          paymentReferenceSavedAt: s.paymentReferenceSavedAt || null,
          status: s.status || 'Pending',
          weight: s.weight ? `${s.weight}kg` : (s.weight || ''),
          numberOfItems: s.numberOfItems ? String(s.numberOfItems) : (s.numberOfItems || '1'),
          email: s.email || '',
          price: typeof s.price === 'number' ? s.price : Number(s.price) || null,
          originalPrice: s.originalPrice,
          customPrice: s.customPrice
        }));
        setShipments(mapped);
        setFilteredShipments(mapped);
      } catch (err) {
        console.error('Failed to fetch shipments', err);
      } finally {
        setLoading(false);
      }
    };
    fetchShipments();
  }, []);

  // Filter shipments based on search term and status
  useEffect(() => {
    let result = shipments;
    
    if (searchTerm) {
      result = result.filter(shipment => 
        shipment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shipment.receiverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shipment.dropoffLocation.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(shipment => shipment.status === statusFilter);
    }
    
    setFilteredShipments(result);
  }, [searchTerm, statusFilter, shipments]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'In Transit': return 'bg-blue-100 text-blue-800';
      case 'Out for Delivery': return 'bg-purple-100 text-purple-800';
      case 'Delivered': return 'bg-green-100 text-green-800';
      case 'Returned': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Pending': return faClock;
      case 'In Transit': return faTruck;
      case 'Out for Delivery': return faMapMarkerAlt;
      case 'Delivered': return faCheckCircle;
      case 'Returned': return faUndo;
      default: return faBox;
    }
  };

  const isPaymentConfirmed = (paymentStatus) => {
    const s = String(paymentStatus || '').toLowerCase();
    return s === 'paid' || s === 'confirmed' || s === 'payment confirmed';
  };

  const getPaymentTypeLabel = (shipment) => {
    if (shipment.paymentType === 'cashOnDelivery') return 'Cash on Delivery (COD)';
    const method = String(shipment.paymentMethod || '').toLowerCase();
    if (method === 'cash' || String(shipment.paymentStatus) === 'Awaiting Cash Payment') return 'Pay in Cash';
    if (method === 'bank_transfer') return 'Bank Transfer';
    return isPaymentConfirmed(shipment.paymentStatus) ? 'Prepaid (Paid)' : 'Prepaid (Pending)';
  };

  const getPaymentTypeIcon = (paymentType) => {
    return paymentType === 'payNow' ? faCreditCard : faMoneyBillWave;
  };

  const getServiceTypeLabel = (serviceType) => {
    return serviceType.charAt(0).toUpperCase() + serviceType.slice(1);
  };

  const handleViewTracking = (shipmentId) => {
    navigate(`/dashboard/user/shipments/tracking/${shipmentId}`);
  };

  const handlePayOnline = async (shipment) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to make a payment.');
        return;
      }
      const email = shipment.email;
      const amount = shipment.price;
      const sid = shipment.shipmentId;
      if (!email || !amount || !sid) {
        alert('Missing payment details. Please contact support.');
        return;
      }
      const init = await paymentsService.initialize({ email, amount, shipmentId: sid }, token);
      const authUrl = init?.data?.authorization_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        throw new Error('Unable to start online payment. Please try again.');
      }
    } catch (err) {
      console.error('Payment initialization failed:', err?.message || err);
      alert(err?.response?.data?.msg || err?.message || 'Failed to initialize payment');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Shipments</h1>
              <p className="mt-1 text-sm text-gray-600">Track and manage all your shipments in one place</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                onClick={() => navigate('/dashboard/user')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back to Dashboard
              </button>
              <Link 
                to="/dashboard/user/shipments/new" 
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                New Shipment
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by ID, receiver, or location..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex items-center">
              <FontAwesomeIcon icon={faFilter} className="text-gray-400 mr-2" />
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="In Transit">In Transit</option>
                <option value="Out for Delivery">Out for Delivery</option>
                <option value="Delivered">Delivered</option>
                <option value="Returned">Returned</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Shipments List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
          </div>
        ) : filteredShipments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faBox} className="text-red-500 text-2xl" />
              </div>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No shipments found</h3>
            <p className="text-gray-500 mb-6">You don't have any shipments matching your search criteria.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={() => navigate('/dashboard/user')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back to Dashboard
              </button>
              <Link 
                to="/dashboard/user/shipments/new" 
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 inline-flex items-center justify-center transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Create New Shipment
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredShipments.map((shipment) => (
              <div 
                key={shipment.id} 
                className="bg-white rounded-lg shadow overflow-hidden border border-gray-200 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Card Header */}
                <div className="p-4 sm:p-5 border-b border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 truncate">{shipment.id}</h3>
                      <p className="text-xs text-gray-500 flex items-center mt-0.5">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-1.5 opacity-70" />
                        {shipment.date}
                      </p>
                    </div>
                    <div className="flex items-center self-start sm:self-auto">
                      <FontAwesomeIcon 
                        icon={getStatusIcon(shipment.status)} 
                        className={`mr-2 text-sm ${
                          shipment.status === 'Pending' ? 'text-yellow-500' :
                          shipment.status === 'In Transit' ? 'text-blue-500' :
                          shipment.status === 'Out for Delivery' ? 'text-purple-500' :
                          shipment.status === 'Delivered' ? 'text-green-500' :
                          'text-red-500'
                        }`} 
                      />
                      <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full ${getStatusColor(shipment.status)}`}>
                        {shipment.status}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Card Body */}
                <div className="p-4 sm:p-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 mt-0.5">
                        <FontAwesomeIcon icon={faUser} className="text-gray-400 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-0.5">Receiver</p>
                        <p className="text-sm font-semibold text-gray-900 truncate">{shipment.receiverName}</p>
                        {shipment.receiverEmail && (
                          <p className="text-xs text-gray-500 truncate">{shipment.receiverEmail}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 mt-0.5">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-0.5">Destination</p>
                        <p className="text-sm font-semibold text-gray-900 line-clamp-1">{shipment.dropoffLocation}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 mt-0.5">
                        <FontAwesomeIcon icon={faTruck} className="text-gray-400 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-0.5">Service Type</p>
                        <p className="text-sm font-semibold text-gray-900">{getServiceTypeLabel(shipment.serviceType)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 mt-0.5">
                        <FontAwesomeIcon icon={getPaymentTypeIcon(shipment.paymentType)} className="text-gray-400 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-0.5">Payment</p>
                        <p className="text-sm font-semibold text-gray-900">{getPaymentTypeLabel(shipment)}</p>
                        {shipment.paymentReferenceCode && (
                          <p className="text-[11px] text-gray-500 truncate">
                            Ref: {shipment.paymentReferenceCode}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start sm:col-span-2">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center mr-3 mt-0.5">
                        <FontAwesomeIcon icon={faBox} className="text-gray-400 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-0.5">Package Details</p>
                        <p className="text-sm font-semibold text-gray-900">{shipment.weight}, {shipment.numberOfItems} item(s)</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Card Footer */}
                <div className="px-4 py-4 sm:px-5 bg-gray-50/50 border-t border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    {/* Payment Status */}
                    <div className="text-xs font-bold uppercase tracking-wide">
                      {isPaymentConfirmed(shipment.paymentStatus) || (shipment.status === 'Delivered' && shipment.paymentType === 'cashOnDelivery') ? (
                        <span className="text-green-600 flex items-center">
                          <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5" />
                          Payment Confirmed
                        </span>
                      ) : (
                        <span className="text-yellow-600 flex items-center">
                          <FontAwesomeIcon icon={faClock} className="mr-1.5" />
                          Payment Pending
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => handleViewTracking(shipment.id)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg flex items-center justify-center text-xs font-bold transition-all shadow-sm"
                      >
                        <FontAwesomeIcon icon={faEye} className="mr-1.5" />
                        Track Shipment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyShipments;
